import React, { useState, useCallback, useRef } from 'react';
import supabase from '../config/supabase';
import { analyzeTemplates, isFingerprintSuppressed, suppressFingerprint } from './subscriptionMisuseDetector';
import SubscriptionMisuseModal from '../components/SubscriptionMisuseModal';
import { isIosShell } from './isIosShell';

/**
 * Shared hook for subscription misuse detection.
 *
 * Usage:
 *   const { runDetection, modalElement } = useMisuseDetection(storeId);
 *
 *   // After a successful DB save or on page open, call:
 *   runDetection(afterCallback, extraTemplates, currentEntityId);
 *     afterCallback    — called once the modal is dismissed (or immediately if skipped)
 *     extraTemplates   — optional { id, name, items }[] to merge with count_templates
 *     currentEntityId  — when provided, only shows the modal when a suspicious pair
 *                        containing this entity id is found; prevents false positives
 *                        on saves that are unrelated to any suspicious group
 *
 *   // In JSX:
 *   return <>{...}{modalElement}</>;
 *
 * Timing: afterCallback is deferred until modal dismissal so parent onSave() cannot
 * unmount the component while the modal is still visible.
 *
 * Suppression: localStorage key `misuseSuppressedFingerprints:<userId>:<storeId>`.
 *
 * Add Store: calls window.startAddPaidStore() when available; fallback navigates to
 * /store-settings.html?startAddStore=1 which auto-invokes the wizard via its query-
 * param listener.
 */
export function useMisuseDetection(storeId) {
  const [modalVisible, setModalVisible] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  const pendingCallbackRef = useRef(null);

  const dismiss = useCallback(() => {
    setModalVisible(false);
    const cb = pendingCallbackRef.current;
    pendingCallbackRef.current = null;
    if (cb) cb();
  }, []);

  const handleContinue = useCallback(() => dismiss(), [dismiss]);

  const handleAddStore = useCallback(() => {
    dismiss();
    if (typeof window.startAddPaidStore === 'function') {
      window.startAddPaidStore();
    } else {
      window.location.href = '/store-settings.html?startAddStore=1';
    }
  }, [dismiss]);

  const handleDontShowAgain = useCallback((fingerprint) => {
    if (storeId && fingerprint) suppressFingerprint(storeId, fingerprint);
    dismiss();
  }, [storeId, dismiss]);

  /**
   * Run the misuse detector.
   *
   * @param {Function} [afterCallback]   — called after modal dismissed or when skipped
   * @param {Array}    [extraTemplates]  — extra { id, name, items } entries merged with
   *                                       active count_templates for analysis
   * @param {*}        [currentEntityId] — when set, the modal only fires if a suspicious
   *                                       pair (score >= 5) containing this entity is found;
   *                                       checked across ALL suspicious pairs, not just the
   *                                       overall best pair
   */
  const runDetection = useCallback(async (afterCallback, extraTemplates, currentEntityId) => {
    try {
      if (!storeId) {
        if (afterCallback) afterCallback();
        return;
      }
      // Never show misuse modal on iOS — it references "store account" in a
      // subscription-related context that Apple would flag under guideline 3.1.1.
      if (isIosShell()) {
        if (afterCallback) afterCallback();
        return;
      }

      // Fetch active count_templates using the existing schema flags.
      // count_templates uses is_active / is_archived / is_deleted booleans,
      // NOT a text "status" column.
      // Also select `items` (legacy jsonb array of inventory_item IDs stored
      // directly on count_templates by CountSessionManager's older save path).
      const { data: rawTemplates, error: fetchErr } = await supabase
        .from('count_templates')
        .select('id, name, items, count_template_items(inventory_item_id, location)')
        .eq('store_id', storeId)
        .eq('is_active', true)
        .eq('is_archived', false)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      if (fetchErr) {
        if (afterCallback) afterCallback();
        return;
      }

      const countTemplates = (rawTemplates || []).map((t) => {
        // Prefer the normalized count_template_items join table (GroupEditor path).
        // Fall back to the legacy `items` jsonb array for templates saved via
        // CountSessionManager; those are plain inventory_item ID values, so map
        // them into the { inventory_item_id } shape the detector expects.
        let items;
        if (t.count_template_items && t.count_template_items.length > 0) {
          items = t.count_template_items;
        } else if (Array.isArray(t.items) && t.items.length > 0) {
          items = t.items.map((id) =>
            (id && typeof id === 'object' && id.inventory_item_id)
              ? id
              : { inventory_item_id: id }
          );
        } else {
          items = [];
        }
        return { id: t.id, name: t.name, items };
      });

      const extras = Array.isArray(extraTemplates) ? extraTemplates : [];
      const allNamed = [...countTemplates, ...extras];

      if (allNamed.length < 2) {
        if (afterCallback) afterCallback();
        return;
      }

      const result = analyzeTemplates(allNamed);

      // Determine which suspicious pair to use for display and RPC.
      // When currentEntityId is provided, find the best-scoring pair that
      // includes that specific entity across ALL suspicious pairs — not just
      // the overall best pair (which may involve unrelated templates).
      let activeScore = result.score;
      let activeReasons = result.reasons;
      let activeFingerprint = result.fingerprint;
      let activeGroup = result.groups[0] || [];

      if (currentEntityId != null) {
        const entityPair = (result.allPairs || []).find(({ pair }) =>
          pair.some((t) => String(t.id) === String(currentEntityId))
        );
        if (!entityPair || entityPair.score < 5) {
          // No suspicious pair contains the just-saved entity — skip modal.
          if (afterCallback) afterCallback();
          return;
        }
        activeScore = entityPair.score;
        activeReasons = entityPair.reasons;
        activeFingerprint = entityPair.fingerprint;
        activeGroup = entityPair.pair;
      }

      // Fire admin notice for high-confidence detections (score >= 8).
      // Only real count_template IDs are sent in p_template_ids.
      if (activeScore >= 8 && activeGroup.length >= 2) {
        const extraIds = new Set(extras.map((e) => String(e.id)));
        const realIds = activeGroup
          .map((t) => t.id)
          .filter((id) => !extraIds.has(String(id)));

        if (realIds.length > 0) {
          supabase.rpc('record_subscription_misuse_notice', {
            p_store_id: storeId,
            p_template_ids: realIds,
            p_template_names: activeGroup.map((t) => t.name),
            p_fingerprint: activeFingerprint,
            p_score: activeScore,
            p_reasons: activeReasons,
          }).then(({ error }) => {
            if (error) console.warn('[MisuseDetector] RPC error:', error.message);
          });
        }
      }

      if (activeScore >= 5 && activeFingerprint && !isFingerprintSuppressed(storeId, activeFingerprint)) {
        pendingCallbackRef.current = afterCallback || null;
        setDetectionResult({ score: activeScore, reasons: activeReasons, fingerprint: activeFingerprint, groups: [activeGroup] });
        setModalVisible(true);
        return;
      }
    } catch (err) {
      console.warn('[MisuseDetector] Detection error:', err.message);
    }
    if (afterCallback) afterCallback();
  }, [storeId]);

  const modalElement = modalVisible && detectionResult
    ? React.createElement(SubscriptionMisuseModal, {
        key: 'misuse-modal',
        onContinue: handleContinue,
        onAddStore: handleAddStore,
        onDontShowAgain: () => handleDontShowAgain(detectionResult.fingerprint),
        templateNames: (detectionResult.groups?.[0] || []).map((t) => t.name),
        score: detectionResult.score,
      })
    : null;

  return { runDetection, modalElement };
}
